import { RentalAgencyChangeStatusEnum } from 'common/enums/rental-agency-change-status.enum';
import { RentalApplicationTypeEnum } from 'common/enums/rental-application-type.enum';
import { RentalPropertyStatusEnum } from 'common/enums/rental-property-status.enum';
import {
  formatRentalAgencyChangeNumber,
  formatRentalAgencyTransitionLabel,
  formatRentalPropertyAgencyLabel,
  formatRentalPropertyNumber,
} from 'common/helpers/rental-property.helper';
import { IRentalAgencyChangeRead } from 'common/services/rental-agency-change-service/rental-agency-change.service.types';
import { IRentalPropertyRead } from 'common/services/rental-property-service/rental-property.service.types';
import { IAddress } from 'common/services/street-service/street.service.types';

export interface IRentalApplicationRow {
  id: string;
  type: RentalApplicationTypeEnum;
  applicationNumber: string;
  detailsUrl: string;
  address: IAddress;
  ownerName: string;
  managingAgencyName: string;
  status: RentalPropertyStatusEnum;
  createdAt: string;
}

const agencyChangeStatusMap: Record<RentalAgencyChangeStatusEnum, RentalPropertyStatusEnum> = {
  [RentalAgencyChangeStatusEnum.Pending]: RentalPropertyStatusEnum.Pending,
  [RentalAgencyChangeStatusEnum.Approved]: RentalPropertyStatusEnum.Approved,
  [RentalAgencyChangeStatusEnum.Rejected]: RentalPropertyStatusEnum.Rejected,
};

const toRegistrationRow = (property: IRentalPropertyRead): IRentalApplicationRow => ({
  id: `${RentalApplicationTypeEnum.Registration}-${property.id}`,
  type: RentalApplicationTypeEnum.Registration,
  applicationNumber: formatRentalPropertyNumber(property.id),
  detailsUrl: `/eiq-manage/rental-portal/properties/${property.id}`,
  address: property.address,
  ownerName: property.ownerName,
  managingAgencyName: formatRentalPropertyAgencyLabel(property.managementType, property.managingAgencyName),
  status: property.status,
  createdAt: property.createdAt,
});

const toAgencyChangeRow = (agencyChange: IRentalAgencyChangeRead): IRentalApplicationRow => ({
  id: `${RentalApplicationTypeEnum.AgencyChange}-${agencyChange.id}`,
  type: RentalApplicationTypeEnum.AgencyChange,
  applicationNumber: formatRentalAgencyChangeNumber(agencyChange.id),
  detailsUrl: `/eiq-manage/rental-portal/agency-changes/${agencyChange.id}`,
  address: agencyChange.address,
  ownerName: agencyChange.ownerName,
  managingAgencyName: formatRentalAgencyTransitionLabel(
    agencyChange.previousManagementType,
    agencyChange.previousManagingAgencyName,
    agencyChange.requestedManagementType,
    agencyChange.requestedManagingAgencyName,
  ),
  status: agencyChangeStatusMap[agencyChange.status],
  createdAt: agencyChange.createdAt,
});

export const toRentalApplicationRows = (
  properties: IRentalPropertyRead[],
  agencyChanges: IRentalAgencyChangeRead[],
): IRentalApplicationRow[] =>
  [...properties.map(toRegistrationRow), ...agencyChanges.map(toAgencyChangeRow)].sort((left, right) =>
    right.createdAt.localeCompare(left.createdAt),
  );
